// src/services/analytics/FirebaseAnalyticsService.js
import { logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import * as firebaseConfig from '../config/firebase.js';
import errorTrackingService from '../errorTracking/ErrorTrackingService.js';

class FirebaseAnalyticsService {
  constructor() {
    this.analytics = firebaseConfig.analytics;
    this.isEnabled = !!firebaseConfig.analytics;
  }

  // User events
  setUser(userId, properties = {}) {
    if (!this.isEnabled) return;
    
    try {
      setUserId(this.analytics, userId);
      if (Object.keys(properties).length > 0) {
        setUserProperties(this.analytics, properties);
      }
      
      // Also set user in error tracking
      errorTrackingService.setUser(userId, properties);
    } catch (error) {
      console.warn('Analytics setUser failed:', error);
      errorTrackingService.trackError({
        type: 'analytics_error',
        message: `Failed to set user: ${error.message}`,
        severity: 'warning',
        context: { userId, properties },
      });
    }
  }

  // App engagement events
  trackAppOpen() {
    this.logEvent('app_open');
  }

  trackScreenView(screenName, screenClass = null) {
    this.logEvent('screen_view', {
      screen_name: screenName,
      screen_class: screenClass || screenName,
    });
  }

  // Learning events
  trackLessonStart(lessonId, moduleId) {
    this.logEvent('lesson_start', {
      lesson_id: lessonId,
      module_id: moduleId,
      content_type: 'lesson',
    });
  }

  trackLessonComplete(lessonId, moduleId, timeSpent = null) {
    this.logEvent('lesson_complete', {
      lesson_id: lessonId,
      module_id: moduleId,
      content_type: 'lesson',
      time_spent: timeSpent,
    });
  }

  trackModuleStart(moduleId) {
    this.logEvent('module_start', {
      module_id: moduleId,
      content_type: 'module',
    });
  }

  trackModuleComplete(moduleId, totalLessons, timeSpent = null) {
    this.logEvent('module_complete', {
      module_id: moduleId,
      content_type: 'module',
      total_lessons: totalLessons,
      time_spent: timeSpent,
    });
  }

  // Tool usage events
  trackToolStart(toolId, toolType) {
    this.logEvent('tool_start', {
      tool_id: toolId,
      tool_type: toolType,
      content_type: 'tool',
    });
  }

  trackToolComplete(toolId, toolType, sessionData = {}) {
    this.logEvent('tool_complete', {
      tool_id: toolId,
      tool_type: toolType,
      content_type: 'tool',
      ...sessionData,
    });
  }

  // Assessment events
  trackAssessmentStart(assessmentType) {
    this.logEvent('assessment_start', {
      assessment_type: assessmentType,
      content_type: 'assessment',
    });
  }

  trackAssessmentComplete(assessmentType, score, insights = null) {
    this.logEvent('assessment_complete', {
      assessment_type: assessmentType,
      content_type: 'assessment',
      score,
      insights,
    });
  }

  // User behavior events
  trackShare(contentType, contentId, method = null) {
    this.logEvent('share', {
      content_type: contentType,
      content_id: contentId,
      method,
    });
  }

  trackSearch(searchTerm, category = null) {
    this.logEvent('search', {
      search_term: searchTerm,
      category,
    });
  }

  trackSettingsChange(settingName, settingValue) {
    this.logEvent('settings_change', {
      setting_name: settingName,
      setting_value: settingValue.toString(),
    });
  }

  // Subscription events
  trackSubscriptionStart(planType) {
    this.logEvent('subscription_start', {
      plan_type: planType,
      content_type: 'subscription',
    });
  }

  trackSubscriptionComplete(planType, amount, currency = 'USD') {
    this.logEvent('purchase', {
      transaction_id: Date.now().toString(),
      value: amount,
      currency,
      item_name: `${planType}_subscription`,
    });
  }

  // Friend Circles events
  trackCircleCreated(circleId, circleName, memberCount = 1) {
    this.logEvent('circle_created', {
      circle_id: circleId,
      circle_name: circleName,
      member_count: memberCount,
      content_type: 'friend_circle',
    });
  }

  trackCircleJoined(circleId, joinMethod = 'invite_code') {
    this.logEvent('circle_joined', {
      circle_id: circleId,
      join_method: joinMethod,
      content_type: 'friend_circle',
    });
  }

  trackJoinRequestSent(circleId, circleName) {
    this.logEvent('join_request_sent', {
      circle_id: circleId,
      circle_name: circleName,
      content_type: 'friend_circle',
    });
  }

  trackMemberApproved(circleId, approvedUserId) {
    this.logEvent('member_approved', {
      circle_id: circleId,
      approved_user_id: approvedUserId,
      content_type: 'friend_circle',
    });
  }

  trackMemberDenied(circleId, deniedUserId) {
    this.logEvent('member_denied', {
      circle_id: circleId,
      denied_user_id: deniedUserId,
      content_type: 'friend_circle',
    });
  }

  trackCircleActivityCreated(circleId, activityType, moduleId = null) {
    this.logEvent('circle_activity_created', {
      circle_id: circleId,
      activity_type: activityType,
      module_id: moduleId,
      content_type: 'friend_circle',
    });
  }

  trackCircleChallengeCompleted(circleId, challengeId, participantCount = 1) {
    this.logEvent('circle_challenge_completed', {
      circle_id: circleId,
      challenge_id: challengeId,
      participant_count: participantCount,
      content_type: 'friend_circle',
    });
  }

  trackCircleReflectionShared(circleId, moduleId, reflectionType) {
    this.logEvent('circle_reflection_shared', {
      circle_id: circleId,
      module_id: moduleId,
      reflection_type: reflectionType,
      content_type: 'friend_circle',
    });
  }

  trackCircleProgressViewed(circleId, memberCount) {
    this.logEvent('circle_progress_viewed', {
      circle_id: circleId,
      member_count: memberCount,
      content_type: 'friend_circle',
    });
  }

  // Error tracking
  trackError(errorType, errorMessage, location = null) {
    this.logEvent('error_occurred', {
      error_type: errorType,
      error_message: errorMessage,
      location,
    });
  }

  // Generic event logger
  logEvent(eventName, parameters = {}) {
    if (!this.isEnabled) {
      console.log(`[Analytics] ${eventName}:`, parameters);
      return;
    }

    try {
      // Ensure parameter values are strings or numbers for Firebase
      const cleanParameters = Object.entries(parameters).reduce((acc, [key, value]) => {
        if (value !== null && value !== undefined) {
          acc[key] = typeof value === 'object' ? JSON.stringify(value) : value;
        }
        return acc;
      }, {});

      logEvent(this.analytics, eventName, cleanParameters);
    } catch (error) {
      console.warn(`Analytics logEvent failed for ${eventName}:`, error);
    }
  }

  // Helper to check if analytics is enabled
  isAnalyticsEnabled() {
    return this.isEnabled;
  }
}

// Create singleton instance
const firebaseAnalyticsService = new FirebaseAnalyticsService();

export default firebaseAnalyticsService;